import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, LogOut, Monitor, Moon, Settings, ShieldCheck, Sun, UserCircle2 } from 'lucide-react';
import { useLogout, type CurrentUser } from '@/lib/auth';
import { useTheme, type Theme } from '@/hooks/useTheme';

/**
 * Profile dropdown for the topbar. Trigger is the avatar (or
 * initials when no avatar); panel surfaces:
 *   - User name + email header
 *   - Profile / Settings / Admin (staff only)
 *   - Theme picker (Light / Dark / System) as radio rows
 *   - Sign out (danger styled)
 *
 * Family-standard UserMenu pattern (visually + behaviourally
 * consistent across sibling products). AnimatePresence handles the
 * mount/unmount transition; a transparent backdrop captures
 * outside-clicks to dismiss.
 */
export function UserMenu({ user }: { user: CurrentUser }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const logout = useLogout();
  const { theme, setTheme } = useTheme();
  const isStaff = user.isStaff === true;

  const initials = (() => {
    const n = (user.displayName || user.email || '?').trim();
    const parts = n.split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return n.slice(0, 2).toUpperCase();
  })();

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={t('nav.user_menu', { defaultValue: 'Account menu' }) as string}
        aria-haspopup="menu"
        aria-expanded={open}
        className="inline-flex items-center gap-2 h-9 ps-1 pe-2 rounded-full hover:bg-muted transition focus:outline-none focus:ring-2 focus:ring-primary/30"
      >
        {user.avatarUrl ? (
          <img
            src={user.avatarUrl}
            alt=""
            className="h-7 w-7 rounded-full object-cover border border-border"
            referrerPolicy="no-referrer"
          />
        ) : (
          <span className="h-7 w-7 inline-flex items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-fuchsia-500 text-white text-[11px] font-semibold">
            {initials}
          </span>
        )}
        <svg viewBox="0 0 12 12" className="h-3 w-3 text-muted-foreground" fill="none" stroke="currentColor" strokeWidth="1.8">
          <path strokeLinecap="round" d="M3 4.5l3 3 3-3" />
        </svg>
      </button>

      <AnimatePresence>
        {open && (
          <>
            <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
            <motion.div
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.12 }}
              className="absolute end-0 mt-2 w-64 z-40 rounded-xl border border-border bg-card shadow-card-lift overflow-hidden"
              role="menu"
            >
              <div className="px-4 py-3 border-b border-border">
                <div className="text-[13.5px] font-semibold text-foreground truncate">
                  {user.displayName || t('nav.signed_in', { defaultValue: 'Signed in' })}
                </div>
                {user.email && (
                  <div className="text-[12px] text-muted-foreground truncate">{user.email}</div>
                )}
              </div>
              <ul className="py-1 text-[13px]">
                <MenuLink onSelect={() => { setOpen(false); navigate('/app/profile'); }}>
                  <UserCircle2 size={14} className="text-muted-foreground" />
                  {t('nav.profile', { defaultValue: 'Profile' })}
                </MenuLink>
                <MenuLink onSelect={() => { setOpen(false); navigate('/app/settings'); }}>
                  <Settings size={14} className="text-muted-foreground" />
                  {t('nav.settings', { defaultValue: 'Settings' })}
                </MenuLink>
                {isStaff && (
                  <MenuLink onSelect={() => { setOpen(false); navigate('/admin'); }}>
                    <ShieldCheck size={14} className="text-muted-foreground" />
                    {t('nav.admin', { defaultValue: 'Admin' })}
                  </MenuLink>
                )}
                <div className="my-1 h-px bg-border" />
                <div className="px-4 pt-1.5 pb-1 text-[10.5px] uppercase tracking-[0.14em] text-muted-foreground font-semibold">
                  {t('nav.theme', { defaultValue: 'Theme' })}
                </div>
                <ThemeRow icon={Sun}     label={t('nav.theme_light', { defaultValue: 'Light' })}  active={theme === 'light'}  onSelect={() => setTheme('light' as Theme)} />
                <ThemeRow icon={Moon}    label={t('nav.theme_dark', { defaultValue: 'Dark' })}    active={theme === 'dark'}   onSelect={() => setTheme('dark' as Theme)} />
                <ThemeRow icon={Monitor} label={t('nav.theme_system', { defaultValue: 'System' })} active={theme === 'system'} onSelect={() => setTheme('system' as Theme)} />
                <div className="my-1 h-px bg-border" />
                <MenuLink danger onSelect={() => { setOpen(false); logout.mutate(); }}>
                  <LogOut size={14} />
                  {t('nav.logout', { defaultValue: 'Sign out' })}
                </MenuLink>
              </ul>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

function MenuLink({
  children, onSelect, danger,
}: {
  children: ReactNode;
  onSelect: () => void;
  danger?: boolean;
}) {
  return (
    <li>
      <button
        type="button"
        role="menuitem"
        onClick={onSelect}
        className={`w-full text-start px-4 py-2 transition flex items-center gap-2 ${
          danger
            ? 'text-rose-600 hover:bg-rose-500/5'
            : 'text-foreground/85 hover:bg-muted hover:text-foreground'
        }`}
      >
        {children}
      </button>
    </li>
  );
}

function ThemeRow({
  icon: IconCmp, label, active, onSelect,
}: {
  icon: typeof Sun;
  label: ReactNode;
  active: boolean;
  onSelect: () => void;
}) {
  return (
    <li>
      <button
        type="button"
        role="menuitemradio"
        aria-checked={active}
        onClick={onSelect}
        className="w-full text-start px-4 py-1.5 transition flex items-center gap-2 text-foreground/85 hover:bg-muted hover:text-foreground"
      >
        <IconCmp size={14} className="text-muted-foreground" />
        <span className="flex-1">{label}</span>
        {active && <Check size={12} className="text-primary" />}
      </button>
    </li>
  );
}
